import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../services/api';
import { Copy, ExternalLink, FileText, CheckCircle, Check } from 'lucide-react';
import { PRICING_PLANS, TRIAL_CONVERSION_PLAN } from '../../constants/pricing';
import './BillingPage.css';

interface UsageInfo {
  monthly_token_limit: number;
  monthly_token_used: number;
  monthly_token_remaining: number;
  pct_used: number;
  requests_per_minute: number;
  key_status: string;
  reset_at: string | null;
}

interface BillingStatus {
  status: string;
  plan: string | null;
  trial_end: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  stripe_customer_id: string | null;
  usage?: UsageInfo;
}

interface Invoice {
  id: string;
  number: string | null;
  created: number;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  description: string | null;
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
  period_start: number;
  period_end: number;
}

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  growth: 'Growth',
  pro: 'Pro',
  enterprise: 'Enterprise',
  admin: 'Admin (Unlimited)',
};

const STATUS_COLOURS: Record<string, string> = {
  paid: '#10b981',
  open: '#f59e0b',
  void: '#6b7280',
  uncollectible: '#ef4444',
};

const BillingPage: React.FC = () => {
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [pauseDays, setPauseDays] = useState<30 | 60 | 90>(30);
  const [showPauseMenu, setShowPauseMenu] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const trialActivated = params.get('trial_activated') === '1';

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/billing/status');
      setBilling(res.data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load billing information.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await apiClient.get('/billing/invoices');
      setInvoices(res.data.invoices || []);
    } catch {
      // Non-fatal — invoices section just stays empty
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBilling();
    fetchInvoices();
  }, [fetchBilling, fetchInvoices]);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const res = await apiClient.post('/billing/portal', {
        return_url: window.location.href,
      });
      if (res.data?.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCheckout = async (selectedPlan?: string) => {
    setCheckoutLoading(true);
    if (selectedPlan) setCheckoutPlan(selectedPlan);
    try {
      const res = await apiClient.post('/billing/checkout', {
        plan: selectedPlan,
        success_url: `${window.location.origin}/billing?success=1`,
        cancel_url: `${window.location.origin}/billing?cancelled=1`,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to start checkout.');
    } finally {
      setCheckoutLoading(false);
      setCheckoutPlan(null);
    }
  };

  const handlePause = async () => {
    setPauseLoading(true);
    try {
      await apiClient.post('/billing/pause', { pause_days: pauseDays });
      setShowPauseMenu(false);
      await fetchBilling();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to pause subscription.');
    } finally {
      setPauseLoading(false);
    }
  };

  const handleResume = async () => {
    setPauseLoading(true);
    try {
      await apiClient.post('/billing/resume');
      await fetchBilling();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to resume subscription.');
    } finally {
      setPauseLoading(false);
    }
  };

  const handleCopyCustomerId = () => {
    if (billing?.stripe_customer_id) {
      navigator.clipboard.writeText(billing.stripe_customer_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const formatUnixDate = (ts: number) =>
    new Date(ts * 1000).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });

  const formatAmount = (cents: number, currency: string) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);

  if (loading) {
    return (
      <div className="billing-page">
        <div className="billing-loading">Loading billing information…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-page">
        <div className="billing-error">
          <p>{error}</p>
          <button onClick={fetchBilling}>Retry</button>
        </div>
      </div>
    );
  }

  const plan = billing?.plan ?? 'trial';
  const planLabel = PLAN_LABELS[plan] ?? plan;
  const isAdmin = plan === 'admin';
  const isTrial = !isAdmin && (billing?.status === 'trialing' || plan === 'trial');
  const usage = billing?.usage;

  return (
    <div className="billing-page">
      <div className="billing-header">
        <h1>Billing &amp; Subscription</h1>
        <p className="billing-subtitle">Manage your plan, usage, and payment details.</p>
      </div>

      {trialActivated && (
        <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 10, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '1.25rem' }}>🎉</span>
          <div>
            <strong style={{ color: 'var(--success)' }}>Trial activated!</strong>
            <p style={{ margin: '2px 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Your $0.99 payment was successful. Your trial runs for 14 days, then {TRIAL_CONVERSION_PLAN.price}{TRIAL_CONVERSION_PLAN.period} kicks in automatically.
            </p>
          </div>
        </div>
      )}

      <div className="billing-cards">
        {/* Current Plan */}
        <div className="billing-card plan-card">
          <h2>Current Plan</h2>
          <div className="plan-badge">{planLabel}</div>
          <ul className="plan-details">
            <li>
              <span>Status</span>
              <span className={`status-pill status-${billing?.status ?? 'none'}`}>
                {billing?.status ?? 'No subscription'}
              </span>
            </li>
            {billing?.trial_end && (
              <li>
                <span>Trial ends</span>
                <span>{formatDate(billing.trial_end)}</span>
              </li>
            )}
            {billing?.current_period_end && (
              <li>
                <span>Next billing date</span>
                <span>{formatDate(billing.current_period_end)}</span>
              </li>
            )}
            {billing?.cancel_at && (
              <li>
                <span>Cancels on</span>
                <span className="cancel-date">{formatDate(billing.cancel_at)}</span>
              </li>
            )}
          </ul>

          {/* Explicit trial conversion notice */}
          {isTrial && billing?.trial_end && (
            <div className="trial-conversion-notice">
              <strong>{TRIAL_CONVERSION_PLAN.price}{TRIAL_CONVERSION_PLAN.period}</strong> starts on <strong>{formatDate(billing.trial_end)}</strong>.{' '}
              <button className="btn-link" onClick={handlePortal} disabled={portalLoading}>
                Cancel before then
              </button>{' '}to avoid being charged.
            </div>
          )}

          {/* Restricted state */}
          {(billing?.status === 'past_due' || billing?.status === 'unpaid') && (
            <div className="billing-restricted-notice">
              <p><strong>Payment failed.</strong> Your account is in read-only mode.</p>
              <button className="btn-primary" onClick={handlePortal} disabled={portalLoading}>
                {portalLoading ? 'Redirecting…' : 'Update Payment Method'}
              </button>
            </div>
          )}

          {!isAdmin && (
            <div className="plan-actions">
              {isTrial ? (
                <button className="btn-primary" onClick={() => handleCheckout()} disabled={checkoutLoading}>
                  {checkoutLoading && !checkoutPlan ? 'Redirecting…' : 'Choose a Plan Below'}
                </button>
              ) : billing?.status === 'paused' ? (
                <button className="btn-secondary" onClick={handleResume} disabled={pauseLoading}>
                  {pauseLoading ? 'Resuming…' : 'Resume Subscription'}
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn-secondary" onClick={handlePortal} disabled={portalLoading}>
                    {portalLoading ? 'Redirecting…' : 'Manage Subscription'}
                  </button>
                  <button className="btn-ghost" onClick={() => setShowPauseMenu(v => !v)} disabled={pauseLoading}>
                    Pause billing
                  </button>
                </div>
              )}

              {showPauseMenu && (
                <div className="pause-menu">
                  <p>Pause billing — your data and content are kept. Features become read-only during the pause.</p>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    {([30, 60, 90] as const).map(d => (
                      <button key={d}
                        className={`pause-day-btn ${pauseDays === d ? 'selected' : ''}`}
                        onClick={() => setPauseDays(d)}>
                        {d} days
                      </button>
                    ))}
                  </div>
                  <button className="btn-secondary" onClick={handlePause} disabled={pauseLoading}>
                    {pauseLoading ? 'Pausing…' : `Pause for ${pauseDays} days`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Customer Number */}
        {billing?.stripe_customer_id && (
          <div className="billing-card customer-card">
            <h2>Customer Number</h2>
            <p className="customer-id-label">Your unique billing reference</p>
            <div className="customer-id-display">
              <code className="customer-id-code">{billing.stripe_customer_id}</code>
              <button
                className="copy-btn"
                onClick={handleCopyCustomerId}
                title="Copy customer ID"
              >
                {copied ? <CheckCircle size={16} color="#10b981" /> : <Copy size={16} />}
              </button>
            </div>
            <p className="customer-id-note">
              Quote this number when contacting support about billing or payment queries.
            </p>
          </div>
        )}

        {/* Usage */}
        {usage && (
          <div className="billing-card usage-card">
            <h2>Token Usage</h2>
            <div className="usage-bar-wrap">
              <div
                className="usage-bar"
                style={{ width: `${Math.min(usage.pct_used, 100)}%` }}
                aria-valuenow={usage.pct_used}
                aria-valuemin={0}
                aria-valuemax={100}
                role="progressbar"
              />
            </div>
            <p className="usage-pct">{usage.pct_used}% used this month</p>
            <ul className="plan-details">
              <li><span>Used</span><span>{usage.monthly_token_used.toLocaleString()} tokens</span></li>
              <li><span>Remaining</span><span>{usage.monthly_token_remaining.toLocaleString()} tokens</span></li>
              <li><span>Monthly limit</span><span>{usage.monthly_token_limit.toLocaleString()} tokens</span></li>
              <li><span>Rate limit</span><span>{usage.requests_per_minute} req/min</span></li>
              {usage.reset_at && (
                <li><span>Resets on</span><span>{formatDate(usage.reset_at)}</span></li>
              )}
            </ul>
          </div>
        )}

        {!usage && billing?.status !== 'no_subscription' && (
          <div className="billing-card usage-card">
            <h2>Token Usage</h2>
            <p className="no-data">No usage data available for your account yet.</p>
          </div>
        )}
      </div>

      {/* Pricing cards — only shown for trial users */}
      {isTrial && (
        <div className="pricing-section">
          <h2 className="pricing-title">Choose a Plan</h2>
          <p className="pricing-subtitle">Upgrade to unlock all features and remove limits.</p>
          <div className="pricing-cards">
            {PRICING_PLANS.map(p => (
              <div key={p.id} className={`pricing-card ${p.featured ? 'pricing-card-featured' : ''}`}>
                {p.featured && <div className="pricing-card-popular">Most Popular</div>}
                <div className="pricing-card-header">
                  <span className="pricing-plan-name">{p.name}</span>
                  <div className="pricing-price"><span className="pricing-amount">{p.price}</span><span className="pricing-period">{p.period}</span></div>
                  <p className="pricing-tagline">{p.desc}</p>
                </div>
                <ul className="pricing-features">
                  {p.features.map(f => (
                    <li key={f}><Check size={14} /><span>{f}</span></li>
                  ))}
                </ul>
                <button
                  className={`pricing-btn ${p.featured ? 'pricing-btn-featured' : ''}`}
                  onClick={() => handleCheckout(p.id)}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading && checkoutPlan === p.id ? 'Redirecting…' : `Select ${p.name}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoice History */}
      <div className="billing-invoices">
        <h2>Billing History</h2>

        {invoicesLoading ? (
          <p className="invoices-loading">Loading invoices…</p>
        ) : invoices.length === 0 ? (
          <p className="no-data">No invoices found. Invoices will appear here once you have an active paid subscription.</p>
        ) : (
          <div className="invoices-table-wrap">
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice #</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id}>
                    <td>{formatUnixDate(inv.created)}</td>
                    <td className="invoice-number">{inv.number || inv.id.slice(-8)}</td>
                    <td className="invoice-desc">{inv.description || `${formatUnixDate(inv.period_start)} – ${formatUnixDate(inv.period_end)}`}</td>
                    <td>{formatAmount(inv.amount_due, inv.currency)}</td>
                    <td>
                      <span
                        className="invoice-status-pill"
                        style={{ background: (STATUS_COLOURS[inv.status] || '#6b7280') + '22', color: STATUS_COLOURS[inv.status] || '#6b7280' }}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td className="invoice-actions">
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="invoice-action-btn" title="Download PDF">
                          <FileText size={15} />
                        </a>
                      )}
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="invoice-action-btn" title="View invoice">
                          <ExternalLink size={15} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
